import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { orderNameBodyTemplateShort, ordersImageBodyTemplate } from './templates';
import OrderNewStore from '../stores/OrderNewStore';

const BodyCounterTemplate = observer(
    ({ product, sdk, orderEditor }: { product: any; sdk: any; orderEditor: OrderNewStore }) => {
        const count = orderEditor.productCount.find((x: any) => x.id === product.id)?.count ?? 0;
        return (
            <>
                <div
                    className="flex justify-content-end gap-1 sm:gap-2 min-w-0 py-2 sm:py-3"
                    style={{
                        flexDirection: window.innerWidth < 300 ? 'column-reverse' : 'row',
                        alignItems: window.innerWidth < 300 ? 'flex-end' : 'center',
                    }}
                >
                    <Button
                        icon="pi pi-minus"
                        outlined
                        rounded
                        size="small"
                        className="p-button-icon-only p-0 w-2rem h-2rem sm:w-2_5rem sm:h-2_5rem"
                        // verhindert Schrumpfen in engen Zellen
                        style={{ flex: '0 0 auto' }}
                        onClick={() => orderEditor.decrementProduct(product)}
                        aria-label="Minus"
                    />

                    <span
                        className="inline-flex align-items-center justify-content-center
                     bg-primary font-bold border-round text-xxs sm:text-xs
                     w-1_5rem h-1_5rem sm:w-2rem sm:h-2rem"
                        style={{ flex: '0 0 auto' }}
                    >
                        {count}
                    </span>

                    <Button
                        icon="pi pi-plus"
                        outlined
                        rounded
                        size="small"
                        severity="success"
                        className="p-button-icon-only p-0 w-2rem h-2rem sm:w-2_5rem sm:h-2_5rem"
                        style={{ flex: '0 0 auto' }}
                        onClick={() => orderEditor.incrementProduct(product)}
                        aria-label="Plus"
                    />
                </div>
            </>
        );
    }
);

const SearchHeader = observer(
    ({
        sdk,
        orderEditor,
        products,
        categories,
        selectedCategory,
    }: {
        sdk: ApplicationSdk;
        orderEditor: OrderNewStore;
        products: any;
        categories: any;
        selectedCategory: any;
    }) => {
        const hasFilters = selectedCategory || orderEditor.productQuickSearch; // Check if any filter is active

        const resetFilters = () => {
            orderEditor.setSelectedCategory(null); // Reset the selected category
            orderEditor.setProductQuickSearch(''); // Reset the search value
        };
        return (
            <div className="flex flex-column gap-2 w-full">
                {products && (
                    <Dropdown
                        placeholder="Filtere nach Kategorie"
                        emptyMessage="Keine Kategorien gefunden"
                        className="w-full"
                        options={products.length > 0 ? categories : []}
                        value={selectedCategory}
                        optionLabel="name"
                        style={{ width: '100%' }}
                        onChange={e => {
                            orderEditor.setSelectedCategory(e.value); // Set the selected category
                        }}
                    />
                )}
                <div style={{ position: 'relative', width: '100%' }}>
                    <InputText
                        placeholder="Produkte durchsuchen"
                        value={orderEditor.productQuickSearch}
                        onChange={e => orderEditor.setProductQuickSearch(e.target.value)}
                        style={{ width: '100%', paddingRight: '3rem' }} // Add padding to avoid overlap with the button
                    />
                    <Button
                        icon="pi pi-search"
                        style={{
                            position: 'absolute',
                            right: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '2rem',
                            width: '2rem',
                            padding: 0,
                        }}
                        onClick={() => {
                            // Trigger search logic here
                            // Todo: Check if we need to do anything on search click
                        }}
                    />
                </div>
                {hasFilters && (
                    <Button
                        label="Filter löschen"
                        className="p-button-text p-button-danger"
                        style={{
                            width: '100%',
                            border: '1px solid #f44336', // Red border
                            color: '#f44336', // Red text
                        }}
                        onClick={resetFilters}
                    />
                )}
            </div>
        );
    }
);

const OrderNew = observer(
    ({ sdk, orderNewStore }: { sdk: ApplicationSdk; orderNewStore: OrderNewStore }) => {
        if (orderNewStore === null) return null;

        if (orderNewStore.orderFinished) {
            return (
                <div
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#d4edda',
                        color: '#155724',
                        padding: '2rem',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        textAlign: 'center',
                        zIndex: 1000,
                        width: '80%',
                        maxWidth: '500px',
                    }}
                >
                    <i
                        className="pi pi-check-circle"
                        style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}
                    ></i>
                    <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Bestellung erfolgreich!</h2>
                    <p style={{ marginTop: '1rem', fontSize: '1rem' }}>
                        Bitte schließe das Fenster.
                    </p>
                </div>
            );
        }

        let products = orderNewStore.allProducts || null;
        const categories = [
            { id: null, name: 'Alle Kategorien' },
            ...(orderNewStore.categories || []),
        ];
        const selectedCategory = orderNewStore.selectedCategory || null;

        if (orderNewStore.isRepresentationObject && !orderNewStore.isRepresentationObjectValid) {
            products = null;
        }
        return (
            <div className="card">
                <Dropdown
                    loading={orderNewStore.isLoading}
                    placeholder={orderNewStore.isLoading ? 'Bitte warten...' : 'Wähle ein Objekt'}
                    emptyMessage={'Keine Objekte gefunden'}
                    className={`w-full ${!orderNewStore.selectedObject ? 'p-invalid' : ''}`}
                    options={orderNewStore.objectList || []}
                    value={orderNewStore.selectedObject || null}
                    optionLabel={'name'}
                    style={{
                        width: '100%',
                        marginBottom: orderNewStore.selectedObject ? '10px' : '0px',
                    }}
                    onChange={e => {
                        orderNewStore.selectObject(e.value);
                    }}
                />
                {!orderNewStore.selectedObject && (
                    <small
                        className="p-error"
                        style={{ display: 'block', marginBottom: '10px' }}
                    >
                        Bitte ein Objekt auswählen
                    </small> // Warnungstext
                )}
                {orderNewStore.isRepresentationObject && (
                    <div
                        className="field"
                        style={{ marginBottom: '1rem' }}
                    >
                        <InputText
                            id="objectName"
                            value={orderNewStore.representationForObject || ''}
                            onChange={e => {
                                orderNewStore.setRepresentationForObject(e.target.value); // Aktualisiere den Namen des Objekts
                            }}
                            placeholder="Objektnamen eingeben"
                            style={{ width: '100%' }}
                            className={`${!orderNewStore.isRepresentationObjectValid ? 'p-invalid' : ''}`}
                        />
                        {!orderNewStore.isRepresentationObjectValid && (
                            <small
                                className="p-error"
                                style={{ display: 'block', marginBottom: '10px' }}
                            >
                                Bitte einen Objekt-Namen eingeben!
                            </small> // Warnungstext
                        )}
                    </div>
                )}
                {products ? (
                    <>
                        <DataTable
                            value={products}
                            size={'small'}
                            showHeaders={false}
                            emptyMessage={'Keine Produkte gefunden'}
                            header={
                                <SearchHeader
                                    sdk={sdk}
                                    orderEditor={orderNewStore}
                                    categories={categories}
                                    products={products}
                                    selectedCategory={selectedCategory}
                                />
                            }
                        >
                            <Column
                                header="Image"
                                style={{ textAlign: 'center' }}
                                body={ordersImageBodyTemplate}
                            ></Column>
                            <Column
                                field="name"
                                header="Name"
                                body={orderNameBodyTemplateShort}
                            ></Column>
                            <Column
                                body={(p: any) => (
                                    <BodyCounterTemplate
                                        product={p}
                                        sdk={sdk}
                                        orderEditor={orderNewStore}
                                    />
                                )}
                            ></Column>
                        </DataTable>
                        <InputTextarea
                            value={orderNewStore.comment}
                            onChange={e => orderNewStore.setComment(e.target.value)}
                            rows={5}
                            cols={30}
                            style={{ width: '100%', marginBottom: '10px' }}
                            placeholder="Kommentar"
                        />
                    </>
                ) : null}
            </div>
        );
    }
);

export default OrderNew;
